// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Footer.js

import React from 'react';
import {
  FaFacebookF as Facebook,
  FaTwitter as Twitter,
  FaInstagram as Instagram,
  FaLinkedinIn as Linkedin,
  FaYoutube as Youtube
} from 'react-icons/fa';
import {
  FiChevronRight as ChevronRight,
  FiMail as Mail,
  FiPhone as Phone,
  FiMapPin as MapPin,
  FiGlobe as Globe
} from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const normalizeHref = (url) => {
  if (!url) return '#';
  const trimmed = String(url).trim();
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed) || trimmed.startsWith('//')) return trimmed;
  if (/^(mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

const Footer = ({ footer = {}, branding = {}, navItems = [], apiBaseURL = '' }) => {
  const { primary, secondary,  fontHeading } = useBrandColors();
  
  const logoSrc = buildResolvedFile(branding?.logo || '', apiBaseURL).resolvedSrc || branding?.logo || '';
  
  // Get institution name
  const institutionName = branding?.title || 'Institution';

  // Get quick links
  const getQuickLinks = () => {
    if (footer?.quickLinks && Array.isArray(footer.quickLinks) && footer.quickLinks.length > 0) {
      return footer.quickLinks.map((link, index) => ({
        id: `quick-link-${index}`,
        label: link.text || link.label || link.name || `Link ${index + 1}`,
        url: link.url || link.href || link.link || '#',
        isCustom: true
      }));
    }

    if (Array.isArray(navItems) && navItems.length > 0) {
      return navItems.map(item => ({
        ...item,
        isCustom: false
      }));
    }

    return [
      { id: 'home', label: 'Home', url: '#', isCustom: false },
      { id: 'about', label: 'About', url: '#about', isCustom: false },
      { id: 'contact', label: 'Contact', url: '#contact', isCustom: false }
    ];
  };

  const quickLinks = getQuickLinks();

  // Social icons mapping
  const socialIcons = {
    facebook: Facebook,
    twitter: Twitter,
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube
  };

  // Get contact email for copyright queries
  const contactEmail = footer?.contactInfo?.email || branding?.email || 'support@institution.edu';

  return (
    <footer className="bg-gray-950 text-white/60 pt-20 pb-8">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-16 border-b border-white/5">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-4 mb-5">
                {logoSrc && (
                  <img
                    src={logoSrc}
                    alt={institutionName}
                    className="h-12 w-auto object-contain"
                  />
                )}
                <div>
                  <h3 
                    className="text-xl font-bold"
                    style={{ 
                      color: '#ffffff',
                      fontFamily: fontHeading,
                    }}
                  >
                    {institutionName}
                  </h3>
                </div>
              </div>
              <p 
                className="text-sm leading-relaxed font-light max-w-sm"
                style={{ color: '#9CA3AF' }}
              >
                {footer?.description || branding?.description || `Committed to excellence in education, innovation, and research.`}
              </p>
              
              {/* Institution Ownership Notice */}
              <div className="mt-6 pt-6 border-t border-white/5">
                <p 
                  className="text-[0.6rem] leading-relaxed font-light"
                  style={{ color: '#6B7280' }}
                >
                  This website is owned and managed by <span style={{ color: secondary }}>{institutionName}</span>.
                  All content, including text, images, and trademarks, is the property of {institutionName}.
                </p>
                <p 
                  className="text-[0.6rem] leading-relaxed font-light mt-2"
                  style={{ color: '#6B7280' }}
                >
                  For copyright-related queries, please contact us at{' '}
                  <a 
                    href={`mailto:${contactEmail}`}
                    className="hover:underline transition-colors"
                    style={{ color: secondary }}
                  >
                    {contactEmail}
                  </a>
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 
                className="font-medium text-xs uppercase tracking-wider mb-5"
                style={{ color: '#ffffff' }}
              >
                Quick Links
              </h4>
              <ul className="space-y-2.5">
                {quickLinks.map((item) => (
                  <li key={item.id}>
                    <a
                      href={normalizeHref(item.url)}
                      className="text-sm transition-colors flex items-center gap-2 group"
                      style={{ color: '#9CA3AF' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                    >
                      <ChevronRight 
                        className="w-3 h-3 transition-colors" 
                        style={{ color: `${secondary}40` }}
                      />
                      <span>{item.label}</span>
                      {item.isCustom && (
                        <span className="text-[0.5rem] ml-1" style={{ color: `${secondary}30` }}>✦</span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            {footer?.contactInfo && (
              <div>
                <h4 
                  className="font-medium text-xs uppercase tracking-wider mb-5"
                  style={{ color: '#ffffff' }}
                >
                  Contact
                </h4>
                <div className="space-y-3.5">
                  {footer.contactInfo.email && (
                    <a
                      href={`mailto:${footer.contactInfo.email}`}
                      className="flex items-center gap-3 text-sm transition-colors group"
                      style={{ color: '#9CA3AF' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{ backgroundColor: `${primary}20` }}
                      >
                        <Mail className="w-3.5 h-3.5" style={{ color: secondary }} />
                      </div>
                      <span className="break-words">{footer.contactInfo.email}</span>
                    </a>
                  )}
                  {footer.contactInfo.phone && (
                    <a
                      href={`tel:${footer.contactInfo.phone}`}
                      className="flex items-center gap-3 text-sm transition-colors group"
                      style={{ color: '#9CA3AF' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{ backgroundColor: `${primary}20` }}
                      >
                        <Phone className="w-3.5 h-3.5" style={{ color: secondary }} />
                      </div>
                      <span>{footer.contactInfo.phone}</span>
                    </a>
                  )}
                  {footer.contactInfo.address && (
                    <div className="flex items-start gap-3 text-sm" style={{ color: '#9CA3AF' }}>
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${primary}20` }}
                      >
                        <MapPin className="w-3.5 h-3.5" style={{ color: secondary }} />
                      </div>
                      <span className="font-light leading-relaxed">{footer.contactInfo.address}</span>
                    </div>
                  )}
                  {footer.contactInfo.mapEmbedUrl && (
                    <a
                      href={footer.contactInfo.mapEmbedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs transition-colors block pl-11"
                      style={{ color: `${secondary}60` }}
                      onMouseEnter={(e) => e.currentTarget.style.color = secondary}
                      onMouseLeave={(e) => e.currentTarget.style.color = `${secondary}60`}
                    >
                      View on Map →
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Social */}
            <div>
              <h4 
                className="font-medium text-xs uppercase tracking-wider mb-5"
                style={{ color: '#ffffff' }}
              >
                Connect
              </h4>
              <div className="flex flex-wrap gap-2 mb-6">
                {footer?.socialMedia && Object.entries(footer.socialMedia).map(([platform, url]) => {
                  if (!url) return null;
                  const Icon = socialIcons[platform];
                  if (!Icon) return null;
                  return (
                    <a
                      key={platform}
                      href={normalizeHref(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 hover:scale-110"
                      style={{ 
                        borderColor: `${primary}30`,
                        color: '#9CA3AF',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = secondary;
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.backgroundColor = `${secondary}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${primary}30`;
                        e.currentTarget.style.color = '#9CA3AF';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      aria-label={`Follow on ${platform}`}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>

              {/* Institution Ownership Badge */}
              <div 
                className="p-4 rounded-xl border"
                style={{ 
                  borderColor: `${primary}20`,
                  backgroundColor: `${primary}05`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-3.5 h-3.5" style={{ color: secondary }} />
                  <span 
                    className="text-[0.6rem] font-medium uppercase tracking-wider"
                    style={{ color: secondary }}
                  >
                    Managed by
                  </span>
                </div>
                <p 
                  className="text-[0.55rem] leading-relaxed font-light"
                  style={{ color: '#6B7280' }}
                >
                  {institutionName} is committed to providing quality education 
                  and maintaining the highest standards of academic excellence.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col gap-1">
              <p 
                className="text-xs font-light"
                style={{ color: '#6B7280' }}
              >
                {footer?.copyrightText || `© ${new Date().getFullYear()} ${institutionName}. All rights reserved.`}
              </p>
              <p 
                className="text-[0.5rem] font-light"
                style={{ color: '#4B5563' }}
              >
                This website is owned and operated by {institutionName}. 
                For copyright issues, contact{' '}
                <a 
                  href={`mailto:${contactEmail}`}
                  className="hover:underline transition-colors"
                  style={{ color: secondary }}
                >
                  {contactEmail}
                </a>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <a
                href="/privacy"
                className="text-xs transition-colors font-light"
                style={{ color: '#6B7280' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#9CA3AF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-xs transition-colors font-light"
                style={{ color: '#6B7280' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#9CA3AF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
              >
                Terms of Service
              </a>
              <a
                href="https://www.truyon.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors font-light"
                style={{ color: '#6B7280' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#9CA3AF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
              >
                Powered by Truyon
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;